/**
 * WebSocket 客户端封装
 *
 * 功能：
 * - 自动连接 / 断线指数退避重连（1s → 2s → 4s → ... → max 30s）
 * - 30s 心跳 ping/pong 检测半开连接
 * - 消息分发：onMessage 回调 + 事件订阅
 * - 优雅关闭：close() 后不再重连
 *
 * 使用方式：
 *   const ws = new RealtimeClient(token);
 *   ws.connect();
 *   ws.send({ type: 'subscribe', role: 'teacher', classId: 'xxx' });
 *   ws.on('student:state', (msg) => { ... });
 *   ws.close();
 */

import type { ClientMessage, ServerMessage } from './realtime-types';

type MessageHandler = (msg: ServerMessage) => void;

// 重连参数
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;
const HEARTBEAT_INTERVAL_MS = 30000;

export class RealtimeClient {
  private ws: WebSocket | null = null;
  private url: string;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private shouldReconnect = true;
  private handlers = new Map<string, Set<MessageHandler>>();
  private globalHandlers = new Set<MessageHandler>();

  /** 连接状态：connecting | connected | disconnected */
  public status: 'connecting' | 'connected' | 'disconnected' = 'disconnected';

  constructor(token: string) {
    // 开发环境走 Vite 代理 /ws，生产环境同源 /ws
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    this.url = `${protocol}//${window.location.host}/ws?token=${encodeURIComponent(token)}`;
  }

  /** 建立 WebSocket 连接 */
  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) return;
    this.shouldReconnect = true;
    this.status = 'connecting';

    try {
      this.ws = new WebSocket(this.url);
      this.ws.onopen = this.onOpen;
      this.ws.onmessage = this.onMessage;
      this.ws.onclose = this.onClose;
      this.ws.onerror = this.onError;
    } catch (err) {
      console.error('[RealtimeClient] WebSocket 创建失败:', err);
      this.scheduleReconnect();
    }
  }

  /** 主动关闭，不再重连 */
  close(): void {
    this.shouldReconnect = false;
    this.clearTimers();
    if (this.ws) {
      this.ws.onclose = null; // 阻止 onclose 触发重连
      this.ws.close();
      this.ws = null;
    }
    this.status = 'disconnected';
  }

  /** 发送消息（连接未就绪时静默丢弃并告警） */
  send(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    } else {
      console.warn('[RealtimeClient] 消息发送失败（未连接）:', msg.type);
    }
  }

  /** 订阅特定类型消息 */
  on(type: string, handler: MessageHandler): () => void {
    if (!this.handlers.has(type)) this.handlers.set(type, new Set());
    this.handlers.get(type)!.add(handler);
    return () => this.handlers.get(type)?.delete(handler);
  }

  /** 订阅所有消息 */
  onAny(handler: MessageHandler): () => void {
    this.globalHandlers.add(handler);
    return () => this.globalHandlers.delete(handler);
  }

  // ===== 内部事件处理 =====

  private onOpen = () => {
    this.reconnectAttempt = 0;
    this.status = 'connected';
    console.log('[RealtimeClient] 已连接');
    this.startHeartbeat();
  };

  private onMessage = (event: MessageEvent) => {
    try {
      const msg = JSON.parse(event.data) as ServerMessage;
      // 分发给特定类型订阅者
      const typeHandlers = this.handlers.get(msg.type);
      if (typeHandlers) typeHandlers.forEach((h) => h(msg));
      // 分发给全局订阅者
      this.globalHandlers.forEach((h) => h(msg));
    } catch (err) {
      console.error('[RealtimeClient] 消息解析失败:', err, event.data);
    }
  };

  private onClose = (event: CloseEvent) => {
    this.status = 'disconnected';
    this.stopHeartbeat();
    console.log(`[RealtimeClient] 连接关闭 (code=${event.code})`);
    if (this.shouldReconnect) this.scheduleReconnect();
  };

  private onError = (event: Event) => {
    console.error('[RealtimeClient] WebSocket 错误:', event);
    // onError 后通常会触发 onClose，由 onClose 处理重连
  };

  // ===== 重连逻辑（指数退避） =====

  private scheduleReconnect(): void {
    if (!this.shouldReconnect) return;
    if (this.reconnectTimer) return; // 已有定时器在等待
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** this.reconnectAttempt, RECONNECT_MAX_MS);
    this.reconnectAttempt++;
    console.log(`[RealtimeClient] ${delay}ms 后重连（第 ${this.reconnectAttempt} 次）`);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  // ===== 心跳 =====

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.send({ type: 'subscribe', role: 'student', classId: '__ping__' }); // 复用 subscribe 做心跳，后端识别 classId='__ping__' 返回 pong
      // 更简单的方案：直接发 ping 字符串
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private clearTimers(): void {
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}

/** 单例实例（延迟创建，首次 connect 时初始化） */
let realtimeClient: RealtimeClient | null = null;

/** 获取 RealtimeClient 单例（需要 token） */
export function getRealtimeClient(token?: string): RealtimeClient | null {
  if (!token) return realtimeClient;
  if (!realtimeClient || realtimeClient.status === 'disconnected') {
    realtimeClient = new RealtimeClient(token);
  }
  return realtimeClient;
}

/** 关闭并销毁单例 */
export function destroyRealtimeClient(): void {
  if (realtimeClient) {
    realtimeClient.close();
    realtimeClient = null;
  }
}
